import React, { useState, useEffect } from 'react';
import EditUserModal from '../components/EditUserModal';
import { obtenerTodosPadres, actualizarUsuarioPerfil } from '../services/matriculaService';
import { registerProfessional, UpdateUsers, updateStatusUser } from '../services/authService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Edit2, X, CheckCircle, Users, Search } from 'lucide-react';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

// Custom Button Component
const Button = ({ 
  children, 
  onClick, 
  className = '', 
  variant = 'primary', 
  size = 'default' 
}) => {
  const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center shadow-sm';
  
  const variantStyles = {
    primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-50',
    green: 'bg-emerald-500 text-white hover:bg-emerald-600',
    danger: 'bg-rose-500 text-white hover:bg-rose-600'
  };

  const sizeStyles = {
    default: 'px-4 py-2 text-sm',
    sm: 'px-3 py-1.5 text-xs',
    lg: 'px-6 py-3 text-base',
    icon: 'p-2',
    xs: 'p-1 text-xs'
  };

  return (
    <button
      onClick={onClick}
      className={`
        ${baseStyles} 
        ${variantStyles[variant]} 
        ${sizeStyles[size]} 
        ${className}
      `}
    >
      {children}
    </button>
  );
};

const UserManagement = ({ registerUser, updateUser, filteredAdmins, filteredProfessionals,  updateAdminsList,
  updateProfessionalsList,   deleteUser}) => {
  const [activeTab, setActiveTab] = useState('professional');
  const [form, setForm] = useState({ name: '', email: '', rol: 'Profesor', cedula: '' });
  const [isRegistering, setIsRegistering] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', name: '', email: '', rol: '', cedula: '', estado: '' });
  const [editPadreForm, setEditPadreForm] = useState({
    id: '',
    nombre: '',
    email: '', 
    cedula: '',
    telefono: '',
    lugar_trabajo: '',
    cargo: '',
    nacionalidad: '',
    estado: ''
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditPadreModalOpen, setIsEditPadreModalOpen] = useState(false);
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
  const [padres, setPadres] = useState([]);
  const [loadingPadres, setLoadingPadres] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  useEffect(() => {
    const cargarPadres = async () => {
      setLoadingPadres(true);
      try {
        const response = await obtenerTodosPadres();
        if(response && response.padres) {
          setPadres(response.padres);
        }
      } catch (error) {
        showErrorToast('Error al cargar la lista de padres');
        console.error('Error:', error);
      } finally {
        setLoadingPadres(false);
        setInitialDataLoaded(true);
      }
    };

    if (!initialDataLoaded) {
      cargarPadres();
    }
  }, [initialDataLoaded]);
  
  const handleDeleteClick = async (user) => {
    if (user.rol === 'Administrador' && filteredAdmins.length <= 1) {
      showWarningToast('No puede desactivar el último administrador');
      return;
    }

    // Primero cerrar cualquier toast abierto
    toast.dismiss();

    // Crear el toast de confirmación con un ID único
    const toastId = `confirm-${user.id_usuario}`;
    
    toast.warn(
      <div>
        <p className="font-bold mb-2">¿Está seguro de {user.estado === 'Desactivada' ? 'activar' : 'desactivar'} al usuario {user.nombre}?</p>
        <p className="mb-4">Esta acción cambiará el estado del usuario.</p>
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => toast.dismiss(toastId)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={async () => {
              toast.dismiss(toastId);
              try {
                const nuevoEstado = user.estado === 'Desactivada' ? 'Activa' : 'Desactivada';
                const result = await updateStatusUser(user.id_usuario, nuevoEstado);
                
                if (result.success) {
                  showSuccessToast(`Usuario ${nuevoEstado === 'Desactivada' ? 'desactivado' : 'activado'} exitosamente`);
                  
                  const usuarioActualizado = {
                    ...user,
                    estado: nuevoEstado
                  };

                  if (user.rol === 'Profesor') {
                    updateProfessionalsList(usuarioActualizado);
                  } else if (user.rol === 'Administrador') {
                    updateAdminsList(usuarioActualizado);
                  } else {
                    setPadres(padres.map(p => 
                      p.id_usuario === user.id_usuario 
                        ? { ...p, estado: nuevoEstado }
                        : p
                    ));
                  }
                } else {
                  showErrorToast(result.message || 'Error al actualizar el estado del usuario');
                }
              } catch (error) {
                showErrorToast('Ocurrió un error al cambiar el estado del usuario');
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {user.estado === 'Desactivada' ? 'Activar' : 'Desactivar'}
          </button>
        </div>
      </div>,
      {
        toastId: toastId,
        position: "top-center",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        closeButton: false,
        className: "rounded-lg",
      }
    );
  };

  const handleEditPadreClick = (padre) => {
    setEditPadreForm({
      id: padre.id_usuario,
      nombre: padre.nombre,
      email: padre.email,
      cedula: padre.cedula,
      telefono: padre.telefono,
      lugar_trabajo: padre.lugar_trabajo,
      cargo: padre.cargo,
      nacionalidad: padre.nacionalidad,
      estado: padre.estado
    });
    setIsEditPadreModalOpen(true);
  };

  const handleEditPadreSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que el nombre tenga al menos nombre y apellido
    if (!validarNombreCompleto(editPadreForm.nombre)) {
      showErrorToast('El nombre debe contener al menos un nombre y un apellido');
      return;
    }
    
    try {
      const datosActualizados = {
        nombre: formatearNombre(editPadreForm.nombre),
        email: editPadreForm.email,
        cedula: editPadreForm.cedula,
        telefono: editPadreForm.telefono,
        lugar_trabajo: editPadreForm.lugar_trabajo,
        cargo: editPadreForm.cargo,
        nacionalidad: editPadreForm.nacionalidad,
        estado: editPadreForm.estado
      };

      const result = await actualizarUsuarioPerfil(editPadreForm.id, datosActualizados);

      if (result.success) {
        showSuccessToast('Padre actualizado exitosamente');
        setIsEditPadreModalOpen(false);
        
        setPadres(padres.map(p => 
          p.id_usuario === editPadreForm.id 
            ? {...p, ...datosActualizados}
            : p
        ));
        
      } else {
        showErrorToast(result.message || 'Error al actualizar el padre');
      }
    } catch (error) {
      console.error('Error al actualizar padre:', error);
      showErrorToast(error.message || 'Error desconocido al actualizar el padre');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const newRol = tab === 'professional' ? 'Profesor' : 'Administrador';
    setForm({ ...form, rol: newRol });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Aplicar formateo automático solo al campo nombre
    if (name === 'name') {
      setForm({ ...form, [name]: formatearNombre(value) });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    // Aplicar formateo automático solo al campo name
    if (name === 'name') {
      setEditForm({ ...editForm, [name]: formatearNombre(value) });
    } else {
      setEditForm({ ...editForm, [name]: value });
    }
  };

  const handleEditPadreInputChange = (e) => {
    const { name, value } = e.target;
    // Aplicar formateo automático solo al campo nombre
    if (name === 'nombre') {
      setEditPadreForm({ ...editPadreForm, [name]: formatearNombre(value) });
    } else {
      setEditPadreForm({ ...editPadreForm, [name]: value });
    }
  };

  // Función para formatear nombres (capitalizar primera letra de cada palabra)
  const formatearNombre = (nombre) => {
    if (!nombre) return '';
    return nombre.toLowerCase().split(' ').map(palabra => 
      palabra.charAt(0).toUpperCase() + palabra.slice(1)
    ).join(' ');
  };

  // Función para validar que el nombre tenga al menos nombre y apellido
  const validarNombreCompleto = (nombre) => {
    if (!nombre || nombre.trim().length === 0) {
      return false;
    }
    const palabras = nombre.trim().split(/\s+/);
    return palabras.length >= 2 && palabras.every(palabra => palabra.length > 0);
  };

  // Función para obtener solo nombre y apellido (primeras dos palabras)
  const obtenerNombreApellido = (nombreCompleto) => {
    if (!nombreCompleto) return 'Nombre no disponible';
    const palabras = nombreCompleto.trim().split(/\s+/);
    return palabras.slice(0, 2).join(' ');
  };

  const validateCedula = (cedula) => {
    if (!/^\d{10}$/.test(cedula)) {
      return false;
    }
  
    const codigoProvincia = parseInt(cedula.slice(0, 2));
    if (codigoProvincia < 1 || codigoProvincia > 24) {
      return false;
    }
  
    const digitoVerificador = parseInt(cedula.slice(-1));
    const coeficientes = [2, 1, 2, 1, 2, 1, 2, 1, 2];
    
    let suma = 0;
    for (let i = 0; i < 9; i++) {
      let resultado = parseInt(cedula[i]) * coeficientes[i];
      suma += resultado > 9 ? resultado - 9 : resultado;
    }
  
    let digitoCalculado = 10 - (suma % 10);
    if (digitoCalculado === 10) {
      digitoCalculado = 0;
    }
  
    return digitoCalculado === digitoVerificador;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const { name, email, cedula } = form;

    // Validar que el nombre tenga al menos nombre y apellido
    if (!validarNombreCompleto(name)) {
      showErrorToast('El nombre debe contener al menos un nombre y un apellido');
      return;
    }
  
    if (!validateCedula(cedula)) {
      showErrorToast('Cédula ecuatoriana inválida');
      return;
    }

    setIsRegistering(true);
    
    try {
      const userData = {
        name: formatearNombre(name),
        email,
        cedula,
        rol: activeTab === 'professional' ? 'Profesor' : 'Administrador',
        estado: 'Activa' // Añadido para cumplir con la nueva estructura de la función
      };

      const result = await registerProfessional(userData);
    
      if (result.success) {
        showSuccessToast('Usuario registrado exitosamente');
        setForm({ name: '', email: '', rol: activeTab === 'professional' ? 'Profesor' : 'Administrador', cedula: '' });
        setIsNewUserModalOpen(false);
        
        const newUser = {
          id_usuario: result.user.id,
          nombre: result.user.nombre,
          email: result.user.email,
          cedula: result.user.cedula,
          rol: result.user.rol,
          estado: result.user.estado
        };

        if (result.user.rol === 'Profesor') {
          updateProfessionalsList(newUser);
        } else {
          updateAdminsList(newUser);
        }
      } else {
        showErrorToast(result.message || 'Error al registrar usuario');
      }
    } catch (error) {
      console.error('Error al registrar usuario:', error);
      if (error.message.includes('sesión')) {
        showErrorToast('Tu sesión ha expirado. Por favor, vuelve a iniciar sesión');
      } else {
        showErrorToast(error.message || 'Error al registrar usuario');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleEditClick = (user) => {
    setEditForm({
      id_usuario: user.id_usuario,
      name: user.nombre,
      email: user.email,
      rol: user.rol,
      cedula: user.cedula,
      estado: user.estado
    });
    setIsEditModalOpen(true);
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
  
    const { id_usuario, name, email, rol, cedula, estado } = editForm;
  
    if (!id_usuario || !name || !email || !rol || !cedula) {
      showErrorToast('Todos los campos son obligatorios');
      return;
    }

    // Validar que el nombre tenga al menos nombre y apellido
    if (!validarNombreCompleto(name)) {
      showErrorToast('El nombre debe contener al menos un nombre y un apellido');
      return;
    }
  
    if (!validateCedula(cedula)) {
      showErrorToast('Cédula ecuatoriana inválida');
      return;
    }
  
    try {
      const token = localStorage.getItem('token');
      const result = await UpdateUsers(id_usuario, formatearNombre(name), email, rol, token, cedula, estado);
      
      if (result.user) {
        showSuccessToast('Usuario actualizado exitosamente');
        setIsEditModalOpen(false);
  
        const updatedUser = { 
          id_usuario, 
          nombre: formatearNombre(name), 
          email, 
          rol, 
          cedula,
          estado: estado || result.user.estado 
        };
  
        if (activeTab === 'professional' && rol === 'Administrador') {
          updateProfessionalsList({ id_usuario, remove: true });
          updateAdminsList(updatedUser);
        } else if (activeTab === 'admin' && rol === 'Profesor') {
          updateAdminsList({ id_usuario, remove: true });
          updateProfessionalsList(updatedUser);
        } else {
          if (rol === 'Profesor') {
            updateProfessionalsList(updatedUser);
          } else {
            updateAdminsList(updatedUser);
          }
        }
      } else {
        showErrorToast(result.message || 'Error al actualizar el usuario');
      }
    } catch (error) {
      if (error.code === 'EMAIL_EXISTS') {
        showErrorToast(error.message || 'El correo electrónico ya está siendo utilizado por otra cuenta.');
      } else if (error.code === 'CEDULA_EXISTS') {
        showErrorToast(error.message || 'La cédula ya está registrada en el sistema.');
      } else {
        showErrorToast(error.message || 'Ocurrió un error al actualizar el usuario.');
      }
    }
  };

  const handleNewUserClick = () => {
    setForm({ 
      name: '', 
      email: '', 
      rol: activeTab === 'professional' ? 'Profesor' : 'Administrador', 
      cedula: '' 
    });
    setIsNewUserModalOpen(true);
  };

  const filterUsers = (users) => {
    if (!searchTerm) return users;
    const searchTermLower = searchTerm.toLowerCase();
    return users.filter(user => 
      user.nombre?.toLowerCase().includes(searchTermLower) ||
      user.email?.toLowerCase().includes(searchTermLower) ||
      user.cedula?.toLowerCase().includes(searchTermLower) ||
      user.telefono?.toLowerCase().includes(searchTermLower)
    );
  };

  const filteredPadresList = filterUsers(padres);
  const filteredProfessionalsList = filterUsers(filteredProfessionals);
  const filteredAdminsList = filterUsers(filteredAdmins);

  const renderTable = (users) => {
    if (!users || users.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4">
              <Users className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron usuarios</h3>
            <p className="text-sm text-gray-500">
              {searchTerm 
                ? "No hay usuarios que coincidan con tu búsqueda" 
                : activeTab === 'professional'
                ? "No hay profesionales registrados"
                : activeTab === 'admin'
                ? "No hay administradores registrados"
                : "No hay usuarios registrados"}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-lg shadow-sm border border-gray-300">
        <table className="min-w-full bg-white">
          <thead>
            <tr>
              <th className="px-6 py-3 bg-gray-100 text-left text-xs font-medium border-gray-500 text-gray-500 uppercase tracking-wider border-b">
                Cedula
              </th>
              <th className="px-6 py-3 bg-gray-100 text-left text-xs font-medium border-gray-500 text-gray-500 uppercase tracking-wider border-b">
                Nombre
              </th>
              <th className="px-6 py-3 bg-gray-100 text-left text-xs font-medium border-gray-500 text-gray-500 uppercase tracking-wider border-b">
                Email
              </th>
              <th className="px-6 py-3 bg-gray-100 text-left text-xs font-medium border-gray-500 text-gray-500 uppercase tracking-wider border-b">
                Estado
              </th>
              <th className="px-6 py-3 bg-gray-100 text-left text-xs font-medium border-gray-500 text-gray-500 uppercase tracking-wider border-b">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 border-gray-500">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors border-gray-500">
                <td className="px-6 py-4 text-sm text-gray-900">{user.cedula}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{obtenerNombreApellido(user.nombre)}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{user.email}</td>
                <td className="px-6 py-4 text-sm">
                  <Button
                    onClick={() => handleDeleteClick(user)}
                    disabled={user.rol === 'Administrador' && filteredAdmins.length <= 1}
                    variant={user.estado === 'Desactivada' ? 'danger' : 'green'}
                    size="sm"
                    className="flex items-center gap-1"
                  >
                    {user.estado === 'Desactivada' ? (
                      <>
                        <X size={14} />
                        Desactivada
                      </>
                    ) : (
                      <>
                        <CheckCircle size={14} />
                        Activa
                      </>
                    )}
                  </Button>
                </td>
                <td className="px-6 py-4 text-sm space-x-3">
                  <button
                    onClick={() => handleEditClick(user)}
                    className="p-1 text-gray-400 hover:text-indigo-500 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Añadir esta función para renderizar las tarjetas en móvil
  const renderMobileCard = (user) => (
    <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-medium text-gray-900">{obtenerNombreApellido(user.nombre)}</h3>
          <p className="text-sm text-gray-500">{user.email}</p>
          <p className="text-xs text-gray-400 mt-1">Cédula: {user.cedula}</p>
        </div>
        <Button
          onClick={() => handleDeleteClick(user)}
          disabled={user.rol === 'Administrador' && filteredAdmins.length <= 1}
          variant={user.estado === 'Desactivada' ? 'danger' : 'green'}
          size="sm"
          className="flex items-center gap-1"
        >
          {user.estado === 'Desactivada' ? (
            <>
              <X size={14} />
              Desactivada
            </>
          ) : (
            <>
              <CheckCircle size={14} />
              Activa
            </>
          )}
        </Button>
      </div>
      <div className="flex justify-end mt-3">
        <button
          onClick={() => handleEditClick(user)}
          className="p-2 text-gray-400 hover:text-indigo-500 transition-colors"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Añadir esta función para renderizar las tarjetas de padres en móvil
  const renderPadreMobileCard = (padre) => (
    <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-medium text-gray-900">{padre.nombre}</h3>
          <p className="text-sm text-gray-500">{padre.email}</p>
          <div className="mt-2 space-y-1">
            <p className="text-xs text-gray-400">Cédula: {padre.cedula}</p>
            <p className="text-xs text-gray-400">Teléfono: {padre.telefono}</p>
          </div>
        </div>
        <Button
          onClick={() => handleDeleteClick(padre)}
          variant={padre.estado === 'Desactivada' ? 'danger' : 'green'}
          size="sm"
          className="flex items-center gap-1"
        >
          {padre.estado === 'Desactivada' ? (
            <>
              <X size={14} />
              Desactivada
            </>
          ) : (
            <>
              <CheckCircle size={14} />
              Activa
            </>
          )}
        </Button>
      </div>
      <div className="flex justify-end mt-3">
        <button
          onClick={() => handleEditPadreClick(padre)}
          className="p-2 text-gray-400 hover:text-indigo-500 transition-colors"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Modificar también la vista móvil
  const renderMobileNoResults = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4">
          <Users className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron usuarios</h3>
        <p className="text-sm text-gray-500">
          {searchTerm 
            ? "No hay usuarios que coincidan con tu búsqueda" 
            : activeTab === 'professional'
            ? "No hay profesionales registrados"
            : activeTab === 'admin'
            ? "No hay administradores registrados"
            : "No hay padres registrados"}
        </p>
        {activeTab !== 'padres' && (
          <Button
            onClick={handleNewUserClick}
            variant="primary"
            className="mt-4"
          >
            <Users className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white rounded-2xl p-2 sm:p-4 shadow-lg">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover={false}
        theme="light"
        className="mt-16"
        style={{ zIndex: 9999 }}
        limit={3}
        enableMultiContainer={false}
      />
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg  overflow-hidden p-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4 sm:gap-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Gestión de Usuarios</h1>
            {activeTab !== 'padres' && (
              <button
                onClick={handleNewUserClick}
                className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 flex items-center justify-center sm:justify-start gap-2 shadow-md hover:shadow-lg"
              >
                <Users className="w-4 h-4" />
                Nuevo Usuario
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 sm:space-x-4 mb-6 sm:mb-8">
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
              <button
                className={`py-2 px-6 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'professional'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
                onClick={() => handleTabChange('professional')}
              >
                Profesores
              </button>
              <button
                className={`py-2 px-6 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'admin'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
                onClick={() => handleTabChange('admin')}
              >
                Admins
              </button>
              <button
                className={`py-2 px-6 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'padres'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
                onClick={() => handleTabChange('padres')}
              >
                Padres
              </button>
            </div>

            <div className="w-full sm:w-auto sm:flex-1 sm:flex sm:justify-end">
              <div className="relative group w-full sm:w-64">
                <input
                  type="text"
                  placeholder="Buscar usuarios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm shadow-sm group-hover:shadow-md text-gray-600 placeholder-gray-400"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Search className="h-5 w-5 text-gray-400 group-hover:text-indigo-500 transition-colors duration-200" />
                </div>
              </div>
            </div>
          </div>

          {/* Vista móvil */}
          <div className="block sm:hidden space-y-4">
            {activeTab === 'professional' && 
              (filteredProfessionalsList.length === 0 
                ? renderMobileNoResults()
                : filteredProfessionalsList.map(user => renderMobileCard(user)))}
            {activeTab === 'admin' && 
              (filteredAdminsList.length === 0
                ? renderMobileNoResults()
                : filteredAdminsList.map(user => renderMobileCard(user)))}
            {activeTab === 'padres' && 
              (loadingPadres 
                ? <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent mx-auto"></div>
                  </div>
                : filteredPadresList.length === 0
                ? renderMobileNoResults()
                : filteredPadresList.map(padre => renderPadreMobileCard(padre)))}
          </div>

          {/* Vista desktop */}
          <div className="hidden sm:block">
            {activeTab === 'professional' && renderTable(filteredProfessionalsList)}
            {activeTab === 'admin' && renderTable(filteredAdminsList)}
            {activeTab === 'padres' && (
              <div className="overflow-hidden rounded-lg shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Cédula
                        </th>
                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Nombre
                        </th>
                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Email
                        </th>
                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Teléfono
                        </th>
                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Estado
                        </th>
                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredPadresList.map((padre) => (
                        <tr key={padre.id_usuario} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-900">{padre.cedula}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{padre.nombre}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{padre.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{padre.telefono}</td>
                          <td className="px-4 py-3 text-sm">
                            <Button
                              onClick={() => handleDeleteClick(padre)}
                              variant={padre.estado === 'Desactivada' ? 'danger' : 'green'} 
                              size="sm"
                              className="flex items-center gap-1"
                            >
                              {padre.estado === 'Desactivada' ? (
                                <>
                                  <X size={14} />
                                  Desactivada
                                </>
                              ) : (
                                <>
                                  <CheckCircle size={14} />
                                  Activa
                                </>
                              )}
                            </Button>
                          </td>
                          <td className="px-4 py-3 text-sm space-x-3">
                            <button
                              onClick={() => handleEditPadreClick(padre)}
                              className="p-1 text-gray-400 hover:text-indigo-500 transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Modales */}
          {isNewUserModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full sm:w-[480px] max-w-full mx-auto shadow-xl p-4 sm:p-8">
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    Nuevo {activeTab === 'professional' ? 'Profesor' : 'Admin'}
                  </h2>
                  <button
                    onClick={() => setIsNewUserModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cédula</label>
                    <input
                      name="cedula"
                      value={form.cedula}
                      onChange={handleInputChange}
                      placeholder="Ingrese la cédula"
                      required
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    />
                    <p className="mt-1 text-xs text-gray-500">La cédula será la contraseña inicial del usuario</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleInputChange}
                      placeholder="Ingrese el nombre completo"
                      required
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleInputChange}
                      placeholder="Ingrese el correo electrónico"
                      required
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-6">
                    <button
                      type="button"
                      onClick={() => setIsNewUserModalOpen(false)}
                      className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium disabled:opacity-50"
                      disabled={isRegistering}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isRegistering}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRegistering ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Registrando...
                        </>
                      ) : (
                        'Registrar'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <EditUserModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            editForm={editForm}
            onInputChange={handleEditInputChange}
            onSubmit={handleEditSubmit}
          />

          {/* Modal para editar padre */}
          {isEditPadreModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full sm:w-[480px] max-w-full mx-auto shadow-xl p-4 sm:p-8">
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Editar Padre</h2>
                  <button
                    onClick={() => setIsEditPadreModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleEditPadreSubmit} className="space-y-4">
                <input
                    name="cedula"
                    value={editPadreForm.cedula}
                    onChange={handleEditPadreInputChange}
                    placeholder="Nombre"
                    className="w-full px-4 py-2 bg-white border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                  <input
                    name="nombre"
                    value={editPadreForm.nombre}
                    onChange={handleEditPadreInputChange}
                    placeholder="Nombre"
                    className="w-full px-4 py-2 bg-white border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                  <input
                    name="email"
                    value={editPadreForm.email}
                    onChange={handleEditPadreInputChange}
                    placeholder="Email"
                    className="w-full px-4 py-2 bg-white border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                  <input
                    name="telefono"
                    value={editPadreForm.telefono}
                    onChange={handleEditPadreInputChange}
                    placeholder="Teléfono"
                    className="w-full px-4 py-2 bg-white border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                  <input
                    name="lugar_trabajo"
                    value={editPadreForm.lugar_trabajo}
                    onChange={handleEditPadreInputChange}
                    placeholder="Lugar de trabajo"
                    className="w-full px-4 py-2 bg-white border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                  <input
                    name="cargo"
                    value={editPadreForm.cargo}
                    onChange={handleEditPadreInputChange}
                    placeholder="Cargo"
                    className="w-full px-4 py-2 bg-white border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                  <input
                    name="nacionalidad"
                    value={editPadreForm.nacionalidad}
                    onChange={handleEditPadreInputChange}
                    placeholder="Nacionalidad"
                    className="w-full px-4 py-2 bg-white border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                  <select
                    name="estado"
                    value={editPadreForm.estado || 'Activa'}
                    onChange={handleEditPadreInputChange}
                    className="w-full px-4 py-2 bg-white border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="Activa">Activa</option>
                    <option value="Desactivada">Desactivada</option>
                  </select>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsEditPadreModalOpen(false)}
                      className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium"
                    >
                      Guardar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;